import { useState, useEffect } from 'react';
import { BallTriangle } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import Nodatafound from 'pages/Common/Nodatafound';
import Card from 'components/card';
import testService from 'ApiService/Student/testServices';
import { formatDate } from 'pages/helpers/dateUtils';

const ExternalMockResults = () => {
  const [loading, setLoading] = useState(true);
  const [mockResults, setMockResults] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await testService.getMyExternalMockResults();
      if (response.data.success) {
        setMockResults(response.data.mockResults);
      }
    } catch (error) {
      toast.error('Failed to fetch mock results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const subjects = [...new Set(mockResults.map((r) => r.subjectName).filter(Boolean))];
  const filteredResults = selectedSubject
    ? mockResults.filter((r) => r.subjectName === selectedSubject)
    : mockResults;

  const stats = {
    total: mockResults.length,
    avg: mockResults.filter((r) => r.Marks != null && r.TotalMarks != null).length
      ? (mockResults.reduce((s, r) => s + (r.Marks / r.TotalMarks) * 100, 0) / mockResults.filter((r) => r.Marks != null && r.TotalMarks != null).length).toFixed(1)
      : 0,
    passCount: mockResults.filter((r) => r.Marks != null && r.TotalMarks != null && (r.Marks / r.TotalMarks) >= 0.35).length,
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[200px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600" />
      <div className="mx-auto -mt-[100px] w-11/12 md:w-3/4">
        <Card extra="w-full p-4">
          <div className="mb-4 flex flex-col space-y-4">
            <div className="w-full md:w-1/2">
              <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                Filter by Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              >
                <option value="">All Subjects</option>
                {subjects.map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            {mockResults.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-blue-50 p-4 dark:bg-navy-800">
                  <h3 className="text-sm font-semibold">Total Tests</h3>
                  <p className="text-lg font-bold">{stats.total}</p>
                </div>
                <div className="rounded-lg bg-green-50 p-4 dark:bg-navy-800">
                  <h3 className="text-sm font-semibold">Average</h3>
                  <p className="text-lg font-bold">{stats.avg}%</p>
                </div>
                <div className="rounded-lg bg-yellow-50 p-4 dark:bg-navy-800">
                  <h3 className="text-sm font-semibold">Passed</h3>
                  <p className="text-lg font-bold">{stats.passCount}</p>
                </div>
              </div>
            )}
          </div>

          {filteredResults.length > 0 ? (
            <div className="space-y-4">
              {filteredResults.map((r) => {
                const pct = r.TotalMarks ? ((r.Marks / r.TotalMarks) * 100).toFixed(1) : 'N/A';
                const pass = r.Marks != null && r.TotalMarks != null && (r.Marks / r.TotalMarks) >= 0.35;
                return (
                  <Card key={r._id} extra="p-4 bg-white dark:bg-navy-800">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">{r.subjectName}</h3>
                        <span className="text-sm text-gray-500">
                          {r.testDate ? formatDate(r.testDate) : '-'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        <div className="rounded-lg bg-blue-50 p-3 dark:bg-navy-700">
                          <h4 className="text-xs font-semibold">Score</h4>
                          <p className="text-xl font-bold">{r.Marks != null ? `${r.Marks}/${r.TotalMarks}` : '-'}</p>
                          <p className="text-sm text-gray-600">{pct}%</p>
                        </div>
                        <div className="rounded-lg bg-green-50 p-3 dark:bg-navy-700">
                          <h4 className="text-xs font-semibold">Correct</h4>
                          <p className="text-xl font-bold text-green-600">{r.correctAnswers ?? '-'}</p>
                        </div>
                        <div className="rounded-lg bg-red-50 p-3 dark:bg-navy-700">
                          <h4 className="text-xs font-semibold">Wrong</h4>
                          <p className="text-xl font-bold text-red-600">{r.wrongAnswers ?? '-'}</p>
                        </div>
                        <div className="rounded-lg bg-purple-50 p-3 dark:bg-navy-700">
                          <h4 className="text-xs font-semibold">Attempt</h4>
                          <p className="text-xl font-bold">{r.attemptNumber ?? 1}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        {r.teacherName && <span>Teacher: {r.teacherName}</span>}
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                          pass
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : r.Marks != null
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {pass ? 'Pass' : r.Marks != null ? 'Fail' : r.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-600 dark:text-gray-400">
              <Nodatafound title="No external mock results found." />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ExternalMockResults;
